import type { SeopLotParam } from '@/types/seop';
import { fmtHours, parseHours, parseUnits } from '@/utils/seopFormat';

/**
 * A edição de um parâmetro de lote, fora do componente porque é regra, não tela — e é a
 * regra que decide o que chega ao banco.
 */

/** O rascunho de uma linha em edição — texto, que é o que se digita. */
export interface RascunhoLote {
  id: string;
  resource: string;
  lot_size: string;
  time_hours: string;
}

export type CampoDoRascunho = 'resource' | 'lot_size' | 'time_hours';

/**
 * Abre o rascunho a partir do que está gravado.
 *
 * As horas entram em `H:MM` porque é assim que a linha é lida — o dado de origem é
 * minuto (`0.383333` é 0:23), e o decimal é só a forma como ele coube no banco.
 */
export function rascunhoDe(lote: SeopLotParam): RascunhoLote {
  return {
    id: lote.id,
    resource: lote.resource,
    lot_size: String(lote.lot_size),
    time_hours: fmtHours(lote.time_hours),
  };
}

/**
 * O que mudou de fato entre a linha gravada e o rascunho — e **só isso** vai no PATCH.
 *
 * Reenviar o campo inteiro parece inofensivo e não é: `2.666667` aparece como `2:40`, e
 * devolver esse texto grava `2.6666666666666665`. O `ceil` do motor transforma diferença
 * de casa decimal em um lote inteiro (DT-B04), então quem não foi tocado não viaja.
 */
export function mudancasDoLote(
  original: SeopLotParam,
  rascunho: RascunhoLote,
): { payload: Partial<SeopLotParam>; erro: string | null } {
  const payload: Partial<SeopLotParam> = {};

  const recurso = rascunho.resource.trim();
  if (!recurso) return { payload, erro: 'Escolha o recurso.' };
  if (recurso !== original.resource) payload.resource = recurso;

  const tamanho = parseUnits(rascunho.lot_size);
  if (tamanho !== original.lot_size) {
    // Lote zero não dá erro no motor: `lots = ceil(unidades/lote)` faz ele PULAR a etapa,
    // e o produto deixa de consumir horas nesse recurso sem aviso nenhum.
    if (!(tamanho > 0)) return { payload, erro: 'O tamanho do lote precisa ser maior que zero.' };
    payload.lot_size = tamanho;
  }

  const texto = rascunho.time_hours.trim();
  if (texto !== fmtHours(original.time_hours)) {
    const horas = parseHours(texto);
    if (!(horas > 0)) return { payload, erro: 'O tempo do lote precisa ser maior que zero.' };
    payload.time_hours = horas;
  }

  return { payload, erro: null };
}
